import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Fade,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { useAuth, SsoSelectAgencyRequest } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Logo from '../components/Logo';
import { blobticMessage } from '../components/BlobticMessage';

interface DossierItem {
  DOS_IDF: string | number;
  DOS_CODE: string;
  DOS_LIB: string;
}

export const SsoCallbackPage: React.FC = () => {
  const { appName } = useParams<{ appName: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { ssoExchange, ssoSelectAgency } = useAuth();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Pour la sélection multiple d'agences
  const [showAgencyModal, setShowAgencyModal] = useState(false);
  const [agencyList, setAgencyList] = useState<DossierItem[]>([]);
  const [selectedAgency, setSelectedAgency] = useState<string>('');
  const [pendingSsoData, setPendingSsoData] = useState<{
    email: string;
    ssoProvider?: string;
    ssoId?: string;
  } | null>(null);

  const hasExchanged = useRef(false);

  useEffect(() => {
    if (hasExchanged.current) return;
    hasExchanged.current = true;

    const query = new URLSearchParams(location.search);
    const code = query.get('code');
    const state = query.get('state');
    const error = query.get('error');
    const errorDesc = query.get('error_description');

    const targetApp = appName || 'Planning';

    if (error) {
      const msg = errorDesc || error || t('login.sso.error');
      setErrorMessage(msg);
      setLoading(false);
      blobticMessage.error(t('login.sso.error'), msg);
      setTimeout(() => navigate(`/${targetApp}/login`, { replace: true }), 3000);
      return;
    }

    if (!code) {
      const msg = t('login.sso.error');
      setErrorMessage(msg);
      setLoading(false);
      setTimeout(() => navigate(`/${targetApp}/login`, { replace: true }), 2500);
      return;
    }

    // Décoder l'état pour extraire le provider et dosCode
    let provider = 'google';
    let dosCode: string | undefined = undefined;

    if (state) {
      try {
        const decoded = JSON.parse(atob(state));
        if (decoded.provider) provider = decoded.provider;
        if (decoded.dosCode) dosCode = decoded.dosCode;
      } catch (e) {
        console.warn('Impossible de décoder le paramètre state:', e);
      }
    }

    // Exécuter l'échange de code côté serveur
    ssoExchange({
      provider,
      code,
      state: state || undefined,
      appName: targetApp,
      dosCode
    }).then((result) => {
      if (result.success) {
        navigate(`/${targetApp}/dashboard`, { replace: true });
      } else if (result.requiresDossierSelection && Array.isArray(result.dossiers) && result.dossiers.length > 0) {
        setAgencyList(result.dossiers);
        setSelectedAgency(result.dossiers[0].DOS_CODE);
        setPendingSsoData({
          email: result.email || '',
          ssoProvider: result.ssoProvider,
          ssoId: result.ssoId
        });
        setShowAgencyModal(true);
        setLoading(false);
      } else {
        const err = result.error || t('login.sso.error');
        setErrorMessage(err);
        setLoading(false);
        blobticMessage.error(t('login.sso.error'), err);
        setTimeout(() => navigate(`/${targetApp}/login`, { replace: true }), 3500);
      }
    }).catch((err) => {
      const errTxt = err?.message || t('login.sso.error');
      setErrorMessage(errTxt);
      setLoading(false);
      blobticMessage.error(t('login.sso.error'), errTxt);
      setTimeout(() => navigate(`/${targetApp}/login`, { replace: true }), 3500);
    });
  }, [location.search, appName, ssoExchange, navigate, t]);

  const handleConfirmAgency = async () => {
    if (!selectedAgency || !pendingSsoData) return;
    setLoading(true);

    const targetApp = appName || 'Planning';
    const requestData: SsoSelectAgencyRequest = {
      email: pendingSsoData.email,
      agency: selectedAgency,
      ssoProvider: pendingSsoData.ssoProvider,
      ssoId: pendingSsoData.ssoId,
      appName: targetApp
    };

    const success = await ssoSelectAgency(requestData);
    if (success) {
      setShowAgencyModal(false);
      navigate(`/${targetApp}/dashboard`, { replace: true });
    } else {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'radial-gradient(ellipse at 50% 50%, #1a2035 0%, #0d111c 100%)'
            : 'radial-gradient(ellipse at 50% 50%, #f4f6fb 0%, #e2e7f3 100%)',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Fade in timeout={600}>
          <Paper
            elevation={4}
            sx={{
              borderRadius: '20px',
              p: { xs: 4, sm: 5 },
              textAlign: 'center',
              boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Logo height={80} />
            </Box>

            {loading && (
              <Box sx={{ py: 3 }}>
                <CircularProgress size={50} thickness={4} sx={{ mb: 3 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                  {t('login.sso.authenticating')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {appName || 'BLOBTIC Platform'}
                </Typography>
              </Box>
            )}

            {!loading && errorMessage && (
              <Box sx={{ py: 2 }}>
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', textAlign: 'left' }}>
                  {errorMessage}
                </Alert>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/${appName || 'Planning'}/login`, { replace: true })}
                  sx={{ textTransform: 'none', borderRadius: '10px' }}
                >
                  {t('login.sso.backToLogin')}
                </Button>
              </Box>
            )}
          </Paper>
        </Fade>
      </Container>

      {/* Modal de sélection d'agence lorsque l'utilisateur a plusieurs agences */}
      <Dialog
        open={showAgencyModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px', p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BusinessIcon color="primary" />
          {t('login.sso.selectAgencyTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {t('login.sso.selectAgencyDesc')}
          </Typography>

          <RadioGroup
            value={selectedAgency}
            onChange={(e) => setSelectedAgency(e.target.value)}
          >
            {agencyList.map((dossier) => (
              <Paper
                key={dossier.DOS_CODE}
                variant="outlined"
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: '12px',
                  borderColor: selectedAgency === dossier.DOS_CODE ? 'primary.main' : 'divider',
                  bgcolor: selectedAgency === dossier.DOS_CODE ? 'action.selected' : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                  }
                }}
                onClick={() => setSelectedAgency(dossier.DOS_CODE)}
              >
                <FormControlLabel
                  value={dossier.DOS_CODE}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {dossier.DOS_LIB || dossier.DOS_CODE}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Code : {dossier.DOS_CODE}
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Paper>
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleConfirmAgency}
            disabled={!selectedAgency || loading}
            sx={{
              py: 1.2,
              borderRadius: '10px',
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            {t('login.sso.confirmAgency')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SsoCallbackPage;
